export default {
  /**
   * @method toArray
   * @description Converts non-array variable to array.
   * @param {Any} variable
   * @return {Array}
   */
  toArray(variable) {
    return Array.isArray(variable) ? variable : [variable];
  },

  /**
   * @method includes
   * @description Checks if provided array includes value/values.
   * @param {Array} array Source array.
   * @param {Array|Any} values Any kind of values (string, number, array).
   * @param {Boolean} strict Comparison strategy. When {true}, should include all values.
   * @return {Boolean}
   */
  includes(array, values, strict = false) {
    const valuesCollection = Array.isArray(values) ? values : [values];
    let includes = false;

    /* Use "for" loop to stop iteration once resolved {true} in non-strict mode */
    for (let i = 0; i < valuesCollection.length; i += 1) {
      const expectedValue = valuesCollection[i];

      if (array.includes(expectedValue)) {
        includes = true;

        if (!strict) {
          break;
        }
      } else if (strict) {
        includes = false;
      }
    }

    return includes;
  },

  /**
   * @method fromNodes
   * @description Converts array-like Object(s) to array.
   * @param {HTMLCollection|NodeList|DOMElement} nodes
   * @return {Array} Array of the provided nodes.
   */
  fromNodes(nodes) {
    if (Array.isArray(nodes)) {
      return nodes;
    }
    return nodes instanceof HTMLCollection || nodes instanceof NodeList
      ? Array.from(nodes)
      : [nodes];
  },

  /**
   * @method merge
   * @description Shallow merge set of String/Array into Array and unqiue.
   * @param {Array} arrays
   * @return {Array}
   */
  merge(...arrays) {
    let merged = [];

    arrays.map((item) => {
      let array = item;
      // eslint-disable-next-line no-unused-expressions
      !Array.isArray(array) && (array = [array]);
      return merged.push(...array);
    });

    merged = this.trim(merged);
    merged = this.unique(merged);

    return merged;
  },

  /**
   * Merge unique data based on object key 'id'.
   *
   * @param  {array} prevItems
   * @param  {array} nextItems
   * @return {array}
   */
  mergeItemsById(prevItems, nextItems) {
    if (prevItems) {
      /* Collect current IDs */
      const ids = prevItems.map((item) => item.id);

      /* Collect just the new unique IDs */
      const unique = nextItems.filter((item) => !ids.includes(item.id));

      /* No new items found */
      if (!unique.length) return prevItems;

      /* Merge items */
      return [...prevItems, ...unique];
    }

    return nextItems;
  },

  /**
   * @method join
   * @description Joins array values using certain delimiter.
   * @param {Array} arrays
   * @return {Array}
   */
  join(...arrays) {
    return this.merge(...arrays).join(" ");
  },

  /**
   * @method unique
   * @description Remove duplicated from the Array entries.
   * @param {Array} array Source array.
   * @return {Array} Reference to the modified array.
   */
  unique: (array) => Array.from(new Set(array)),

  /**
   * @method trim
   * @description Removes empty values from Array.
   * @param {Array} array
   * @return {Array}
   */
  trim(array) {
    return array.filter(Boolean);
  },

  /* Find and remove item from an array */
  /**
   * @method remove
   * @description Find and remove item from an array.
   * @param {Array} array Source array.
   * @param {Any} item Item to remove.
   * @return {Array}
   */
  remove(array, item) {
    const i = array.indexOf(item);

    if (i !== -1) {
      array.splice(i, 1);
    }

    return array;
  },

  /**
   * Get items before current item
   *
   * @param {Array} array Source array.
   * @param {Number|String} item Current item.
   * @return {Array}
   */
  getPreviousItems(arr, item) {
    const itemIndex = arr.indexOf(item);
    let result = arr;

    if (itemIndex !== -1) {
      result = arr.filter((_arrItem, index) => index < itemIndex);
    }

    return result;
  },

  /**
   * @method flatten
   * @description Flattens passed array into one-level array.
   * @param {Array} array
   * @return {Array}
   */
  flatten(array) {
    if (Array.isArray(array)) {
      return array.reduce((done, current) => done.concat(current), []);
    }

    return array;
  },

  /**
   * @method sum
   * @description Counts sum of an array of numbers
   * @param {Array} array
   * @return {Number}
   */
  sum(array) {
    return array.reduce((a, b) => a + b, 0);
  },

  /**
   * @param  {array} arr        array of aray of any objects/numbers/...
   * @param  {number} fromIndex index of item which will be moved
   * @param  {number} toIndex   index where item will be moved to
   * @return {array}            return sorted array
   */
  move(array, fromIndex, toIndex) {
    const item = array[fromIndex];
    array.splice(fromIndex, 1);
    array.splice(toIndex, 0, item);
    return array;
  },

  /**
   * Creates an array of elements split into groups the length of 'chunkSize'.
   * If array can't be split evenly, the final chunk will be the remaining
   * elements.
   *
   * @param  {array} arr Example: ['a', 'b', 'c', 'd']
   * @param  {Number} chunkSize Example: 2
   * @return {array} Example output: [['a', 'b'], ['c', 'd']]
   */
  toChunks(arr, chunkSize) {
    const result = [];

    for (let i = 0; i < arr.length; i += chunkSize) {
      result.push(arr.slice(i, i + chunkSize));
    }

    return result;
  },

  /**
   * Check if all elements of 'items' array belong to the
   * same chunked array group created by 'chunkSize'.
   *
   * @param {Array} items Example: ['c, d']
   * @param {Array} arr Example: ['a', 'b', 'c', 'd']
   * @param {Number} chunkSize Example: 2
   * @returns {Boolean} True if items are is a same chunk group
   */
  isInSameGroup({ items, arr, chunkSize }) {
    const groups = this.toChunks(arr, chunkSize);
    const firstItemGroup = groups.find((group) => group.indexOf(items[0]) !== -1);
    const result = this.includes(firstItemGroup, items, true);

    return result;
  },
};
